/**
 * DEV-LOCAL 전용 — 로컬 Python 스크래핑(백엔드 /startTask/sg-py/* 프록시) 호출 래퍼.
 *
 * 운영에서는 백엔드가 SG_PY_SCRAPING_URL 미설정 → 비활성(호출 시 에러)이며,
 * 프론트도 isLocalDevelopment() 에서만 이 경로를 노출한다.
 */
import { apiClient } from '@/shared/api/client';

export interface SgPyRequestQrResult {
  mstIdx: number | null;
  sessionId: string | null;
  swkQrRef: string | null;
  qrDeeplink: string | null;
  qrCodeBase64: string | null;
  expiresAt: number | null;
  success: boolean;
  error: string | null;
}

export interface SgPyQrStatus {
  status: string | null;
  scanned: boolean;
  done: boolean;
  expired: boolean;     // C# TTimer(50s) 상한 초과 또는 Singpass session_expired
  success: boolean;
  error: string | null;
}

export interface SgPyAuthResult {
  sessionId: string | null;
  authenticated: boolean;
  filingAgentName?: string | null;
  success: boolean;
  error: string | null;
}

export interface SgPyPwdRequestResult {
  mstIdx: number | null;
  sessionId: string | null;
  maskedMobileNumber: string | null;
  formFactors: string[];
  success: boolean;
  error: string | null;
}

export interface SgPyChangeFyeResult {
  success: boolean;
  result: string | null;
  csp: string | null;
  regenerate: string | null;
  error: string | null;
}

export interface SgPyReservedName {
  transaction_id: string;
  proposed_entity_name: string;
  proposed_suffix: string;
  /** L_BASECODE 로 변환된 접미사 표시명 (예: "PTE. LTD."). 없으면 코드 값. */
  suffix_name?: string;
  entity_type: string;
  entity_subtype: string;
  primary_ssic_code: string;
  primary_ssic_desc: string;
  status: string;
  purpose: string;
}

export interface SgPyReservedNamesResult {
  reservedNames: SgPyReservedName[];
  success: boolean;
  error: string | null;
}

export interface SgPyIncorporationResult {
  success: boolean;
  draftId: string | null;
  result: string | null;
  error: string | null;
}

function body<T>(res: unknown): T {
  return ((res as any)?.data ?? {}) as T;
}

export const sgPyScrapingApi = {
  async enabled(): Promise<boolean> {
    try {
      const res = await apiClient.get('/startTask/sg-py/enabled');
      return !!(res as any)?.data?.enabled;
    } catch {
      return false;
    }
  },

  async requestQr(taskId?: string | number): Promise<SgPyRequestQrResult> {
    const res = await apiClient.post('/startTask/sg-py/request-qr', { taskId });
    return body<SgPyRequestQrResult>(res);
  },

  async qrStatus(sessionId: string): Promise<SgPyQrStatus> {
    const res = await apiClient.get(
      `/startTask/sg-py/qr-status?sessionId=${encodeURIComponent(sessionId)}`,
    );
    return body<SgPyQrStatus>(res);
  },

  async responseQr(p: {
    sessionId: string;
    filingAgentUEN: string;
    mstIdx: number | null;
  }): Promise<SgPyAuthResult> {
    const res = await apiClient.post('/startTask/sg-py/response-qr', p);
    return body<SgPyAuthResult>(res);
  },

  async pwdRequest(p: {
    singpassId: string;
    password: string;
    mstIdx: number | null;
    taskId?: string | number;
  }): Promise<SgPyPwdRequestResult> {
    const res = await apiClient.post('/startTask/sg-py/pwd-request', p);
    return body<SgPyPwdRequestResult>(res);
  },

  async pwdResponse(p: {
    sessionId: string;
    otp: string;
    filingAgentUEN: string;
    mstIdx: number | null;
  }): Promise<SgPyAuthResult> {
    const res = await apiClient.post('/startTask/sg-py/pwd-response', p);
    return body<SgPyAuthResult>(res);
  },

  async changeFye(p: {
    taskId?: string | number;
    sessionId: string;
    mstIdx: number | null;
    entityUEN: string;
    clientName: string;
    revisedFinancialYearEndDate: string;
    financialYearEndPeriod?: string;
    changeReasons?: string[];
    otherReason?: string;
  }): Promise<SgPyChangeFyeResult> {
    const res = await apiClient.post('/startTask/sg-py/change-fye', p);
    return body<SgPyChangeFyeResult>(res);
  },

  async reservedNames(sessionId: string, mstIdx: number | null): Promise<SgPyReservedNamesResult> {
    const res = await apiClient.post('/startTask/sg-py/reserved-names', { sessionId, mstIdx });
    return body<SgPyReservedNamesResult>(res);
  },

  async createIncorporation(p: {
    taskId?: string | number;
    sessionId: string;
    uniqueId: string;
    mstIdx: number | null;
    payload: Record<string, unknown>;
  }): Promise<SgPyIncorporationResult> {
    const res = await apiClient.post('/startTask/sg-py/incorporation-create', p);
    return body<SgPyIncorporationResult>(res);
  },
};
